import { Injectable, inject } from '@angular/core';
import { SupabaseService } from './supabase.service';
import { DashboardData, FieldPhoto, FloraRecord, Project, Site, RecordChange } from './models';

const RECORD_FIELDS = `id,project_id,site_id,common_name,scientific_name,responsible,fecha,
received_at,updated_at,activity,latitude,longitude,gps_accuracy,utm_zone,utm_hemisphere,
utm_easting,utm_northing,stratum,condition,individuals,coverage,height_m,dn_db_cm,notes`;

@Injectable({ providedIn: 'root' })
export class FloraDataService {
  private readonly db = inject(SupabaseService);

  async dashboard(): Promise<DashboardData> {
    const client = this.db.client;
    const [projects, sites, records, photos, recent] = await Promise.all([
      client.from('proyectos').select('id', { count: 'exact', head: true }),
      client.from('sitios_transectos').select('id', { count: 'exact', head: true }),
      client.from('registros_flora').select('id', { count: 'exact', head: true }),
      client.from('registro_fotos').select('id', { count: 'exact', head: true }),
      client.from('registros_flora').select(RECORD_FIELDS).order('received_at', { ascending: false }).limit(6)
    ]);
    for (const result of [projects, sites, records, photos, recent]) {
      if (result.error) throw result.error;
    }
    return {
      projects: projects.count ?? 0,
      sites: sites.count ?? 0,
      records: records.count ?? 0,
      photos: photos.count ?? 0,
      recent: (recent.data ?? []) as FloraRecord[]
    };
  }

  async projectsAndSites(): Promise<{ projects: Project[]; sites: Site[] }> {
    const client = this.db.client;
    const [p, s] = await Promise.all([
      client.from('proyectos').select('id,name,responsible,description,created_at').order('created_at', { ascending: false }),
      client.from('sitios_transectos').select('id,project_id,name,type,description,created_at').order('created_at', { ascending: false })
    ]);
    if (p.error) throw p.error;
    if (s.error) throw s.error;
    return { projects: (p.data ?? []) as Project[], sites: (s.data ?? []) as Site[] };
  }

  async records(): Promise<FloraRecord[]> {
    const { data, error } = await this.db.client.from('registros_flora')
      .select(RECORD_FIELDS).order('received_at', { ascending: false }).limit(200);
    if (error) throw error;
    return (data ?? []) as FloraRecord[];
  }

  /** Recupera por páginas todos los registros visibles para el usuario (RLS vigente).
   * Evita el límite de 200 registros de la vista de tarjetas y el límite API habitual
   * de 1000 filas por consulta. El mapa acota a 20 000 elementos por rendimiento.
   */
  async mapRecords(): Promise<FloraRecord[]> {
    const result: FloraRecord[] = [];
    const pageSize = 1000;
    for (let page = 0; page < 20; page++) {
      const { data, error } = await this.db.client.from('registros_flora')
        .select(RECORD_FIELDS).order('received_at', { ascending: false })
        .range(page * pageSize, (page + 1) * pageSize - 1);
      if (error) throw error;
      const batch = (data ?? []) as FloraRecord[];
      result.push(...batch);
      if (batch.length < pageSize) break;
    }
    return result;
  }

  /** Consulta paginada completa para informes y exportaciones; nunca exporta silenciosamente
   * una muestra truncada. El tope protege la memoria del navegador.
   */
  async reportRecords(): Promise<FloraRecord[]> {
    const result: FloraRecord[] = [];
    const pageSize = 1000;
    const maximum = 50000;
    for (let start = 0; start <= maximum; start += pageSize) {
      const { data, error } = await this.db.client.from('registros_flora')
        .select(RECORD_FIELDS).order('received_at', { ascending: false })
        .order('id', { ascending: false }).range(start, start + pageSize - 1);
      if (error) throw error;
      const batch = (data ?? []) as FloraRecord[];
      if (start + batch.length > maximum) {
        throw new Error('Hay más de 50 000 registros. Acota el informe por proyecto o periodo.');
      }
      result.push(...batch);
      if (batch.length < pageSize) return result;
    }
    throw new Error('El informe supera el límite de 50 000 registros.');
  }

  async record(id: string): Promise<FloraRecord> {
    const { data, error } = await this.db.client.from('registros_flora')
      .select(RECORD_FIELDS).eq('id', id).single();
    if (error) throw error;
    return data as FloraRecord;
  }

  async photos(recordId: string): Promise<FieldPhoto[]> {
    const { data, error } = await this.db.client.from('registro_fotos')
      .select('id,record_id,photo_type,remote_path')
      .eq('record_id', recordId).order('created_at', { ascending: true });
    if (error) throw error;
    const photos = (data ?? []) as FieldPhoto[];
    // El bucket es PRIVADO: no usamos getPublicUrl ni cambiamos las políticas.
    return Promise.all(photos.map(async photo => {
      const { data: signed, error: signError } = await this.db.client.storage
        .from('flora-fotografias').createSignedUrl(photo.remote_path, 3600);
      return {
        ...photo,
        signedUrl: signed?.signedUrl,
        error: signError?.message
      };
    }));
  }

  /** Metadatos fotográficos de registros seleccionados. No descarga blobs ni expone rutas. */
  async photoMetadata(ids: string[]): Promise<{data: Array<{record_id: string}> | null; error: Error | null}> {
    if (!ids.length) return { data: [], error: null };
    const rows: Array<{record_id: string}> = [];
    for (let offset = 0; ; offset += 1000) {
      const { data, error } = await this.db.client.from('registro_fotos')
        .select('record_id,id').in('record_id', ids).order('id').range(offset, offset + 999);
      if (error) return { data: null, error: error as Error };
      rows.push(...(data ?? []).map((p: { record_id: string }) => ({record_id: String(p.record_id)})));
      if ((data ?? []).length < 1000) break;
    }
    return { data: rows, error: null };
  }

  async changes(recordId: string): Promise<RecordChange[]> {
    const { data, error } = await this.db.client.from('monitoreo_cambios')
      .select('id,action,actor_id,happened_at').eq('record_id', recordId)
      .order('happened_at', { ascending: false }).limit(20);
    if (error) throw error;
    return (data ?? []) as RecordChange[];
  }

}
